import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ReferencePicker from './ReferencePicker';
import MaskEditor from './MaskEditor';
import BaseImagePicker from './BaseImagePicker';
import {
  AiImageArchiveOutputItem,
  AiImageEditSourceInput,
  AiImageGeneration,
  AiImageReferenceInput,
  AiImageStudioConfig,
  AiImageStudioDraft,
  DamSearchAssetResult,
  ProductReferenceLookupResult,
} from '../types';
import { reuseAiImageOutput } from '../aiImageStudioApi';
import { getAiImageAssetUrl } from '../utils';
import { getGenerationBlockedReason } from '../studioPresentation';
import { estimateAiImageCostText } from '../costEstimate';
import { usePasteReferenceImages, PasteNotifySeverity } from '../hooks/usePasteReferenceImages';
import { ICategory } from '../../../types/damTypes';

type EditIntent = 'remove' | 'replace' | 'product' | 'detail' | 'cleanup';

interface EditingPanelProps {
  config: AiImageStudioConfig | null;
  categories: ICategory[];
  history: AiImageGeneration[];
  editSource: AiImageEditSourceInput | null;
  generating: boolean;
  onEditSourceChange: (source: AiImageEditSourceInput | null) => void;
  onGenerateDraft: (draft: AiImageStudioDraft) => void;
  onSearchDamAssets: (query: string, categoryId?: string) => Promise<DamSearchAssetResult[]>;
  onLookupSkuReference: (sku: string) => Promise<ProductReferenceLookupResult>;
  onNotify: (message: string, severity: PasteNotifySeverity) => void;
}

const EDIT_INTENT_OPTIONS: Array<{ value: EditIntent; label: string }> = [
  { value: 'product', label: 'Placera produkt' },
  { value: 'remove', label: 'Ta bort objekt' },
  { value: 'replace', label: 'Ersätt objekt' },
  { value: 'detail', label: 'Modifiera detalj' },
  { value: 'cleanup', label: 'Cleanup/blenda' },
];

function makeReferenceId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error(`Kunde inte läsa filen ${file.name}.`));
    reader.readAsDataURL(file);
  });
}

function getSourceImageUrl(source: AiImageEditSourceInput | null): string {
  if (!source) return '';
  if (source.dataUrl) return source.dataUrl;
  return getAiImageAssetUrl(source.url || source.thumbnailUrl) || '';
}

function buildPrompt(intent: EditIntent, instruction: string, hasProductReference: boolean): string {
  const trimmedInstruction = instruction.trim();
  const base = trimmedInstruction || 'Utför en kontrollerad redigering i det markerade området.';
  const preserve = [
    'Preserve everything outside the masked area exactly: composition, camera angle, lighting, color profile, texture, sharpness, and all existing details.',
    'Do not degrade image quality outside the mask.',
  ].join(' ');

  if (intent === 'product') {
    return [
      'Place the referenced product naturally inside the masked area.',
      hasProductReference
        ? 'Use the attached product reference as the exact visual source for packaging, proportions, label, logo, colors, typography, and material finish.'
        : 'If no product reference is available, follow the user instruction carefully.',
      'Integrate the product with believable perspective, scale, contact shadow, ambient occlusion, reflections, and matching light direction.',
      'The product must not look pasted in.',
      preserve,
      `User instruction: ${base}`,
    ].join('\n\n');
  }

  if (intent === 'remove') {
    return [
      'Remove only the object or disturbance inside the masked area.',
      'Fill the area with natural background that matches surrounding texture, lighting, grain, shadows, and perspective.',
      preserve,
      `User instruction: ${base}`,
    ].join('\n\n');
  }

  if (intent === 'replace') {
    return [
      'Replace only the masked content with the requested object or visual change.',
      'Match the surrounding scene with natural edges, shadows, reflections, scale, and texture continuity.',
      preserve,
      `User instruction: ${base}`,
    ].join('\n\n');
  }

  if (intent === 'cleanup') {
    return [
      'Clean up only the masked area.',
      'Blend edges, shadows, color, texture, and noise so the edit is invisible.',
      preserve,
      `User instruction: ${base}`,
    ].join('\n\n');
  }

  return [
    'Modify only the masked detail according to the user instruction.',
    'Keep the edit photorealistic and consistent with the original scene.',
    preserve,
    `User instruction: ${base}`,
  ].join('\n\n');
}

const EditingPanel: React.FC<EditingPanelProps> = ({
  config,
  categories,
  history,
  editSource,
  generating,
  onEditSourceChange,
  onGenerateDraft,
  onSearchDamAssets,
  onLookupSkuReference,
  onNotify,
}) => {
  const [references, setReferences] = useState<AiImageReferenceInput[]>([]);
  const [basePickerOpen, setBasePickerOpen] = useState(false);
  const [maskDataUrl, setMaskDataUrl] = useState<string | null>(null);
  const [intent, setIntent] = useState<EditIntent>('product');
  const [instruction, setInstruction] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');

  const sourceImageUrl = getSourceImageUrl(editSource);
  const editModels = useMemo(
    () => (config?.models || []).filter((model) => model.provider === 'openai' || model.provider === 'gemini'),
    [config]
  );
  const selectedModel = editModels.find((model) => model.id === selectedModelId) || editModels[0] || null;
  const usesReferencesForIntent = intent === 'product' || intent === 'replace';
  const activeReferences = usesReferencesForIntent ? references : [];
  const hasProductReference = activeReferences.some((reference) => reference.type === 'sku_product' || reference.type === 'dam_asset');
  const resolvedPrompt = useMemo(
    () => buildPrompt(intent, instruction, hasProductReference),
    [hasProductReference, instruction, intent]
  );

  useEffect(() => {
    setMaskDataUrl(null);
    setReferences([]);
  }, [editSource?.referenceId]);

  useEffect(() => {
    if (!editModels.length) {
      setSelectedModelId('');
      return;
    }
    if (!selectedModelId || !editModels.some((model) => model.id === selectedModelId)) {
      const preferred = editModels.find((model) => model.provider === 'openai') || editModels[0];
      setSelectedModelId(preferred.id);
    }
  }, [editModels, selectedModelId]);

  const addReferences = (nextReferences: AiImageReferenceInput[]) => {
    const maxReferences = config?.maxReferences || 14;
    setReferences((prev) => [...prev, ...nextReferences].slice(0, maxReferences));
  };

  const handleUploadSource = async (file: File) => {
    const dataUrl = await readFileAsDataUrl(file);
    onEditSourceChange({
      referenceId: makeReferenceId('edit-upload'),
      type: 'upload',
      fileName: file.name,
      title: file.name,
      mimeType: file.type || 'image/jpeg',
      dataUrl,
    });
  };

  const handleSelectDamBase = (asset: DamSearchAssetResult) => {
    onEditSourceChange({
      referenceId: makeReferenceId('edit-dam'),
      type: 'dam_asset',
      assetId: asset._id,
      url: asset.previewUrl,
      thumbnailUrl: asset.thumbnailUrl,
      title: asset.title || asset.fileName,
      fileName: asset.fileName,
      mimeType: asset.fileType,
    });
  };

  const handleSelectArchiveBase = (item: AiImageArchiveOutputItem) => {
    const output = item.output;
    onEditSourceChange({
      referenceId: makeReferenceId('edit-archive'),
      type: 'generated',
      generationId: item.generationId,
      outputId: output._id,
      url: output.publicUrl,
      thumbnailUrl: output.thumbnailUrl || output.mediumUrl || output.publicUrl,
      title: item.prompt.slice(0, 80) || output.fileName,
      fileName: output.fileName,
      mimeType: output.mimeType,
    });
  };

  const handleAddUploadedReferences = async (files: FileList | File[]) => {
    const fileList = Array.from(files);
    const nextReferences = await Promise.all(
      fileList.map(async (file) => ({
        referenceId: makeReferenceId('edit-ref-upload'),
        type: 'upload' as const,
        fileName: file.name,
        title: file.name,
        mimeType: file.type || 'image/jpeg',
        dataUrl: await readFileAsDataUrl(file),
      }))
    );
    addReferences(nextReferences);
  };

  const pasteBlockedReason = !editSource || !sourceImageUrl
    ? 'Välj en basbild innan du klistrar in referenser.'
    : usesReferencesForIntent
      ? undefined
      : 'Referensbilder används bara för Placera produkt och Ersätt objekt.';

  usePasteReferenceImages({
    enabled: true,
    remainingSlots: Math.max((config?.maxReferences || 14) - references.length, 0),
    blockedReason: pasteBlockedReason,
    onAddFiles: handleAddUploadedReferences,
    onNotify,
  });

  const handleAddDamReference = (asset: DamSearchAssetResult) => {
    addReferences([
      {
        referenceId: makeReferenceId('edit-ref-dam'),
        type: 'dam_asset',
        assetId: asset._id,
        url: asset.previewUrl,
        thumbnailUrl: asset.thumbnailUrl,
        title: asset.title,
        fileName: asset.fileName,
        mimeType: asset.fileType,
      },
    ]);
  };

  const handleAddSkuReference = (asset: DamSearchAssetResult, sku: string) => {
    addReferences([
      {
        referenceId: makeReferenceId('edit-ref-sku'),
        type: 'sku_product',
        assetId: asset._id,
        sku,
        url: asset.previewUrl,
        thumbnailUrl: asset.thumbnailUrl,
        title: asset.title,
        fileName: asset.fileName,
        mimeType: asset.fileType,
      },
    ]);
  };

  const handleAddGeneratedReference = async (generationId: string, outputId: string) => {
    const reference = await reuseAiImageOutput(generationId, outputId);
    addReferences([
      {
        referenceId: reference.referenceId || makeReferenceId('edit-ref-generated'),
        type: 'generated',
        generationId: reference.generationId,
        outputId: reference.outputId,
        url: reference.url,
        thumbnailUrl: reference.thumbnailUrl,
        title: reference.title,
        fileName: reference.fileName,
        mimeType: reference.mimeType,
      },
    ]);
  };

  const editCostEstimateText = selectedModel?.provider === 'openai'
    ? estimateAiImageCostText(config, {
      provider: 'openai',
      model: selectedModel.id,
      prompt: resolvedPrompt,
      presetId: '',
      aspectRatio: 'Auto',
      imageSize: config?.imageSizes?.includes('2K') ? '2K' : (config?.imageSizes?.[0] || '1K'),
      quality: config?.openAiQualities?.includes('high') ? 'high' : 'auto',
      outputFormat: selectedModel.outputFormats.includes('jpeg') ? 'jpeg' : (selectedModel.outputFormats[0] || 'png'),
      count: 1,
      editMode: 'inpaint',
      references: activeReferences,
    })
    : null;

  const editBlockedReason = getGenerationBlockedReason({
    view: 'edit',
    prompt: instruction,
    hasEditSource: Boolean(editSource && sourceImageUrl),
    hasMask: Boolean(maskDataUrl),
    hasModel: Boolean(selectedModel),
  });

  const handleGenerate = () => {
    if (editBlockedReason || !editSource || !maskDataUrl || !selectedModel) return;

    const imageSize = selectedModel.provider === 'gemini'
      ? '1K'
      : config?.imageSizes?.includes('2K') ? '2K' : (config?.imageSizes?.[0] || '1K');
    onGenerateDraft({
      provider: selectedModel.provider,
      model: selectedModel.id,
      prompt: resolvedPrompt,
      presetId: '',
      workflow: 'standard',
      aspectRatio: 'Auto',
      imageSize,
      quality: config?.openAiQualities?.includes('high') ? 'high' : 'auto',
      outputFormat: selectedModel.outputFormats.includes('jpeg') ? 'jpeg' : (selectedModel.outputFormats[0] || 'png'),
      count: 1,
      editMode: 'inpaint',
      editSource,
      editMask: {
        dataUrl: maskDataUrl,
        mimeType: 'image/png',
        fileName: 'inpaint-mask.png',
      },
      preserveOriginalOutsideMask: true,
      references: activeReferences,
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Redigering
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Välj en bild, måla området som får ändras och beskriv slutresultatet.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<PhotoLibraryIcon />}
              onClick={() => setBasePickerOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              {editSource ? 'Byt basbild' : 'Välj basbild'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {!editSource || !sourceImageUrl ? (
        <Alert severity="info">
          Välj basbild här: ladda upp, sök i Athena eller hämta från arkivet. Du kan också öppna en bild från Arkiv med Redigera, eller från Asset view.
        </Alert>
      ) : (
        <>
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap' }} useFlexGap>
                <Chip label={editSource.type === 'dam_asset' ? 'Athena-basbild' : editSource.type === 'generated' ? 'Arkivbasbild' : 'Uppladdad basbild'} size="small" />
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {editSource.title || editSource.fileName || 'Basbild'}
                </Typography>
                <Button size="small" color="inherit" onClick={() => setBasePickerOpen(true)}>
                  Byt bild
                </Button>
                <Button size="small" color="inherit" onClick={() => onEditSourceChange(null)}>
                  Ta bort
                </Button>
              </Stack>
              <MaskEditor imageUrl={sourceImageUrl} onMaskChange={setMaskDataUrl} />
            </CardContent>
          </Card>

          {usesReferencesForIntent ? (
            <ReferencePicker
              references={references}
              history={history}
              categories={categories}
              maxReferences={config?.maxReferences || 14}
              onAddUploadedFiles={handleAddUploadedReferences}
              onSearchDamAssets={onSearchDamAssets}
              onLookupSkuReference={onLookupSkuReference}
              onAddDamAssetReference={handleAddDamReference}
              onAddSkuProductReference={handleAddSkuReference}
              onAddGeneratedReference={handleAddGeneratedReference}
              onRemoveReference={(referenceId) => setReferences((prev) => prev.filter((reference) => reference.referenceId !== referenceId))}
            />
          ) : (
            <Alert severity="info">
              Referensbilder används bara för Placera produkt och Ersätt objekt. För {intent === 'remove' ? 'Ta bort objekt' : intent === 'cleanup' ? 'Cleanup/blenda' : 'Modifiera detalj'} skickas endast basbild, mask och instruktion.
            </Alert>
          )}

          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack spacing={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel id="ai-edit-model-label">Redigeringsmodell</InputLabel>
                  <Select
                    labelId="ai-edit-model-label"
                    value={selectedModel?.id || ''}
                    label="Redigeringsmodell"
                    onChange={(event) => setSelectedModelId(event.target.value)}
                  >
                    {editModels.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.label} {model.provider === 'openai' ? '(precision mask)' : '(visuell mask)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel id="ai-edit-intent-label">Redigeringstyp</InputLabel>
                  <Select
                    labelId="ai-edit-intent-label"
                    value={intent}
                    label="Redigeringstyp"
                    onChange={(event) => setIntent(event.target.value as EditIntent)}
                  >
                    {EDIT_INTENT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {editCostEstimateText && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, px: 0.5 }}>
                      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', flexShrink: 0 }}>
                        Uppskattad kostnad
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary', textAlign: 'right' }}>
                        {editCostEstimateText}
                      </Typography>
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<AutoAwesomeIcon />}
                    onClick={handleGenerate}
                    disabled={Boolean(editBlockedReason)}
                    sx={{
                      py: 1.25,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      background: editBlockedReason ? undefined : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                      boxShadow: editBlockedReason ? 'none' : '0 6px 16px rgba(15,52,96,0.28)',
                    }}
                  >
                    {generating && !editBlockedReason ? 'Lägg redigering i kö' : 'Generera redigerad bild'}
                  </Button>
                  {editBlockedReason && (
                    <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 0.5, fontSize: '0.8rem', color: 'text.secondary' }}>
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      {editBlockedReason}
                    </Typography>
                  )}
                </Box>
                <TextField
                  label="Instruktion"
                  placeholder="Ex: Placera produkten på bordet, vinklad mot kameran, med mjuk skugga åt höger."
                  multiline
                  minRows={4}
                  value={instruction}
                  onChange={(event) => setInstruction(event.target.value)}
                  fullWidth
                />
                <Alert severity="info" icon={<AutoFixHighIcon />}>
                  {selectedModel?.provider === 'gemini'
                    ? 'Nano Banana/Gemini får det markerade området med lite omgivning, plus en svartvit maskguide. Vitt betyder ytan som får ändras.'
                    : 'OpenAI får det markerade området med lite omgivning, plus en alpha-mask. Genomskinligt betyder ytan som får ändras.'}
                  {' '}Resultatet läggs tillbaka i originalbilden. Allt utanför markeringen behålls.
                </Alert>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}
      <BaseImagePicker
        open={basePickerOpen}
        categories={categories}
        onClose={() => setBasePickerOpen(false)}
        onSelectUpload={(file) => { void handleUploadSource(file); }}
        onSelectDamAsset={handleSelectDamBase}
        onSelectSkuAsset={(asset) => handleSelectDamBase(asset)}
        onSelectArchiveOutput={handleSelectArchiveBase}
        onSearchDamAssets={onSearchDamAssets}
        onLookupSkuReference={onLookupSkuReference}
      />
    </Box>
  );
};

export default EditingPanel;
